import { Chip, emo, FS, FL } from "@/components/ch/ui";
import { authKey, btnLabel, freqLabel, hcol, health, nextDays, rowCd, rowCol, srt, stageIdx, stageLabel, STAGES, URG, urgKey, waitText, done } from "@/components/compliances/shared";

const minDays = (a) => Math.min(9999, ...a.filter((r) => !done(r)).map(nextDays));
const HealthBar = ({ h }) => (h === null ? <small style={{ marginLeft: "auto" }}>—</small> : <><span className="hb"><i style={{ "--p": `${h}%`, "--k": hcol(h) }} /></span><small>{h}%</small></>);

function Row({ r, onOpen, onAction }) {
  return (
    <div className="rr" onClick={() => onOpen(r)} data-testid={`register-row-${r.id}`}>
      <div className="c1"><span className="ic">{emo(r.category)}</span><div style={{ minWidth: 0 }}><b>{r.name}</b><small>{freqLabel(r)}{r.is_custom && <> · <span data-testid={`internal-badge-${r.id}`}>Internal</span></>}</small></div></div>
      <div className="c2"><b data-testid={`next-due-${r.id}`}>{r.next_due ? (r.next_due.slice(0, 4) === String(new Date().getFullYear()) ? FS(r.next_due) : FL(r.next_due)) : r.projected_due ? <span title="Expected from the obligation's frequency">Expected {FL(r.projected_due)}</span> : <span className="mu" style={{ fontWeight: 400 }}>No upcoming due date</span>}</b><Chip c={rowCol(r)} id={`status-chip-${r.id}`}>{rowCd(r)}</Chip></div>
      <div className="c3">{r.owner ? `${r.current?.handling === "va_assist" ? "🧑‍💼" : "👤"} ${r.owner}` : "Unassigned"}</div>
      <div className="c4"><span className="dot" style={{ "--c": rowCol(r) }} /><span><b>{stageLabel(r)}</b><small>{waitText(r)}</small></span></div>
      <div className="c5"><button className={`b sm ${r.needs_you ? "cta" : ""}`} onClick={(e) => { e.stopPropagation(); onAction(r); }} data-testid={`row-action-${r.id}`}>{btnLabel(r)}</button></div>
    </div>
  );
}

function Group({ k, rows, byAuth, collapsed, toggle, onOpen, onAction }) {
  const n = rows.filter((r) => r.needs_you).length;
  return (
    <section className="gc" data-testid={`group-${k}`}>
      <div className="gh" onClick={() => toggle(k)} data-testid={`group-toggle-${k}`}><span className="ga">{collapsed ? "▸" : "▾"}</span><b>{k}</b><small>{rows.length} obligation{rows.length > 1 ? "s" : ""}{n ? ` · ${n} need you` : ""}</small>{byAuth && <HealthBar h={health(rows)} />}</div>
      {!collapsed && [...rows].sort(srt).map((r) => <Row key={r.id} r={r} onOpen={onOpen} onAction={onAction} />)}
    </section>
  );
}

export function ListView({ rows, gb, coll, toggle, onOpen, onAction }) {
  const g = {};
  rows.forEach((r) => { const k = gb === "auth" ? authKey(r) : urgKey(r); (g[k] = g[k] || []).push(r); });
  const keys = gb === "auth" ? Object.keys(g).sort((a, b) => minDays(g[a]) - minDays(g[b])) : URG.filter((k) => g[k]);
  return (
    <div data-testid="register-list">
      <div className="rhd"><span>Obligation</span><span>Next due</span><span>Owner</span><span>Status</span><span /></div>
      {keys.map((k) => <Group key={k} k={k} rows={g[k]} byAuth={gb === "auth"} collapsed={coll[k]} toggle={toggle} onOpen={onOpen} onAction={onAction} />)}
    </div>
  );
}

export function BoardView({ rows, onOpen }) {
  return (
    <div className="bd" data-testid="register-board">
      {STAGES.map((t, k) => {
        const a = rows.filter((r) => (r.current || done(r)) && (done(r) ? 6 : stageIdx(r)) === k);
        return (
          <div className="bc" key={t} data-testid={`board-col-${k}`}>
            <div className="bt"><b>{t}</b><span className="chip">{a.length}</span></div>
            {a.map((r) => (
              <div key={r.id} className="bk" style={{ "--c": rowCol(r) }} onClick={() => onOpen(r)} data-testid={`board-card-${r.id}`}>
                <b>{emo(r.category)} {r.name}</b><small>{authKey(r)} · {r.owner || "Unassigned"}</small>
                <div className="r sp"><Chip c={rowCol(r)}>{rowCd(r)}</Chip><small>{waitText(r)}</small></div>
              </div>
            ))}
            {!a.length && <small>Nothing here</small>}
          </div>
        );
      })}
    </div>
  );
}

export function AuthorityView({ rows, onPick }) {
  const g = {};
  rows.forEach((r) => (g[authKey(r)] = g[authKey(r)] || []).push(r));
  return (
    <div className="ag" data-testid="register-by-authority">
      {Object.keys(g).sort((a, b) => minDays(g[a]) - minDays(g[b])).map((k) => {
        const a = g[k], h = health(a), n = a.filter((r) => r.needs_you).length, nd = minDays(a);
        return (
          <button key={k} className="ac" onClick={() => onPick(k)} data-testid={`authority-card-${k}`} title={a[0].authority?.full_name}>
            <svg viewBox="0 0 44 44" width="64" height="64" aria-hidden="true"><circle cx="22" cy="22" r="18" fill="none" stroke="var(--bd)" strokeWidth="5" />
              {h !== null && <circle cx="22" cy="22" r="18" fill="none" stroke={hcol(h)} strokeWidth="5" strokeLinecap="round" strokeDasharray={`${h * 1.131} 113.1`} transform="rotate(-90 22 22)" />}
              <text x="22" y="26" textAnchor="middle" fontSize="11" fontWeight="700" fill="currentColor" data-testid={`authority-health-${k}`}>{h === null ? "—" : `${h}%`}</text></svg>
            <div><b>{k}</b><small>{a.length} obligation{a.length > 1 ? "s" : ""}</small><small>{nd < 9999 ? `Next due ${FS(a.find((r) => nextDays(r) === nd).next_due)}` : "All compliant"}</small>{n > 0 && <Chip c="var(--wa)">{n} need you</Chip>}</div>
          </button>
        );
      })}
    </div>
  );
}
